'use client'

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  ImageList,
  ImageListItem,
} from '@mui/material'
import { useRef, useState, ChangeEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import ImageViewerDialog from 'components/ImageViewerDialog'
import { assignApi } from 'hooks/api/assign'
import { AssignFile } from 'types/assignFile'

interface Props {
  assignId: string
  hasSecurityReport: boolean
}

export default function ImageUpload({
  assignId,
  hasSecurityReport,
}: Props): JSX.Element {
  const { t } = useTranslation(['page-assign-components', 'common'])
  const [previews, setPreviews] = useState<AssignFile[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(
    null,
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const MAX_IMAGES = 10
  const isDeleteDialogOpen = deleteTargetIndex !== null // 削除確認ダイアログの表示状態（削除対象が選ばれているかどうか）

  const fetchImages = async () => {
    setIsLoading(true)
    try {
      const res = await assignApi().getImages(assignId)
      setPreviews(res.data.imageUrls)
    } catch (err) {
      console.error('画像取得失敗', err)
      setPreviews([])
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchImages()
  }, [assignId])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    // 選択されたファイルから、残りのアップロード可能数だけを切り出す（最大10枚まで）
    const newFiles = Array.from(files).slice(0, MAX_IMAGES - previews.length)
    setIsLoading(true)
    if (fileInputRef.current) {
      fileInputRef.current.value = '' // アップロード後にファイル入力をリセット
    }

    for (const file of newFiles) {
      try {
        await assignApi().uploadImageWithPresignedUrl(assignId, file)
      } catch (err) {
        console.error('アップロード失敗', err)
      }
    }

    await fetchImages()
  }

  const handleRemove = async (index: number) => {
    const target = previews[index]
    try {
      await assignApi().deleteImage(assignId, target.id)
    } catch (err) {
      console.error('削除失敗', err)
      return
    }

    // 指定された index の画像だけを除いて previews を更新（削除）
    setPreviews((prev) => prev.filter((_, i) => i !== index))
    setHasSubmitted(false)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      await assignApi().submitImage(assignId)
      setHasSubmitted(true)
    } catch (err) {
      console.error('画像送信に失敗しました', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClickImage = (url: string) => () => {
    setSelectedImage(url)
  }

  const handleClickDeleteIcon = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    setDeleteTargetIndex(index)
  }

  const handleDialogClose = () => {
    setSelectedImage(null)
  }

  return (
    <Box sx={imageBoxStyle}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        multiple
        onChange={handleUpload}
      />

      <ImageList cols={3} gap={8} sx={{ marginBottom: 2 }}>
        {previews.map((preview, index) => (
          <ImageListItem
            key={preview.id}
            sx={imageListItemStyle}
            onClick={handleClickImage(preview.imageUrl)}
          >
            <Box
              sx={{
                backgroundImage: `url(${preview.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%',
                height: '100%',
              }}
            />
            <IconButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                handleClickDeleteIcon(e, index)
              }
              sx={deleteIconStyle}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </ImageListItem>
        ))}
        {isLoading && (
          <Box sx={loadingStyle}>
            <CircularProgress />
          </Box>
        )}
      </ImageList>

      <Button
        variant="outlined"
        fullWidth
        onClick={handleUploadClick}
        startIcon={<AddPhotoAlternateIcon />}
        sx={{ marginBottom: 2, height: 48, fontWeight: 'bold', fontSize: 16 }}
        disabled={previews.length >= MAX_IMAGES}
      >
        {t('picture.upload_button')}
      </Button>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        sx={{ height: 48, fontWeight: 'bold', fontSize: 16 }}
        disabled={!hasSecurityReport || isSubmitting || hasSubmitted}
      >
        {t('common:buttons.submit')}
      </Button>

      <ImageViewerDialog imageUrl={selectedImage} onClose={handleDialogClose} />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setDeleteTargetIndex(null)}
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center' }}>
          <Box sx={{ mb: 2, fontSize: '20px' }}>
            {t('picture.confirm_delete')}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="error"
              sx={{
                minWidth: 140,
                height: 55,
                fontWeight: 'bold',
                fontSize: 20,
              }}
              onClick={async () => {
                if (deleteTargetIndex === null) return
                await handleRemove(deleteTargetIndex)
                setDeleteTargetIndex(null)
              }}
            >
              {t('common:buttons.delete')}
            </Button>
            <Button
              variant="outlined"
              sx={{
                minWidth: 140,
                height: 55,
                fontWeight: 'bold',
                fontSize: 20,
              }}
              onClick={() => setDeleteTargetIndex(null)}
            >
              {t('common:buttons.cancel')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

const imageBoxStyle = {
  border: '1px dashed #ccc',
  padding: 2,
  borderRadius: 2,
  marginTop: 2,
}

const imageListItemStyle = {
  aspectRatio: '1 / 1',
  borderRadius: 1,
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
}

const deleteIconStyle = {
  position: 'absolute',
  top: 4,
  right: 4,
  backgroundColor: '#888',
  border: '2px solid #fff',
  color: '#fff',
  '&:hover': { backgroundColor: '#666' },
}

const loadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 200,
  width: '100%',
}
